import { filesUrl, supabase } from "./supabase";

export interface Beat { i: number; t: number; bar: number; beat: number; chord: string; implied?: boolean }
export interface Span { label: string; start: number; end: number; start_beat: number; end_beat: number; implied?: boolean }
export interface Word { t: number; w: string; syl?: [number, number][] }
export interface Line { t: number; end: number | null; text: string; words: Word[] }

export interface Analysis {
  title: string;
  artist: string;
  duration: number;
  source: { videoId?: string | null; url?: string | null };
  analysis: {
    bpm: number; key: string | null; beatsPerBar: number; capo: number;
    capoShapes?: string[]; tuningCents?: number; engine: string;
  };
  beats: Beat[];
  chords: Span[];
  lyrics: { meta: Record<string, unknown> | null; lines: Line[] };
}

export interface SongRow {
  id: string; slug: string; title: string | null; artist: string | null;
  credits: string[] | null; artwork: string | null;
  status: string; stage: string | null; analysis_key: string | null;
  duration: number | null; bpm: number | null; music_key: string | null;
  capo: number | null; lyrics_mode: string | null; duplicate_of: string | null;
}

/**
 * Fetch a song's analysis and overlay any human fixes.
 *
 * The JSON is immutable per engine version, so it is cached hard by the CDN and
 * by the browser; corrections are applied on top at read time rather than
 * rewritten into the file, which keeps the cached object shared by everyone.
 */
export async function loadAnalysis(song: SongRow): Promise<Analysis | null> {
  if (!song.analysis_key) return null;
  const response = await fetch(`${filesUrl}/${song.analysis_key}`, { cache: "force-cache" });
  if (!response.ok) return null;
  const analysis: Analysis = await response.json();

  const { data: fixes } = await supabase
    .from("corrections_current")
    .select("beat, chord")
    .eq("song_id", song.duplicate_of ?? song.id);

  return fixes?.length ? applyCorrections(analysis, fixes) : analysis;
}

export function applyCorrections(
  analysis: Analysis,
  fixes: { beat: number; chord: string }[],
): Analysis {
  const byBeat = new Map(fixes.map((f) => [f.beat, f.chord]));
  const beats = analysis.beats.map((b) =>
    byBeat.has(b.i) ? { ...b, chord: byBeat.get(b.i)! } : b);

  // Spans have to be rebuilt: a corrected beat may join or split a run.
  const chords: Span[] = [];
  let start = 0;
  for (let i = 1; i <= beats.length; i++) {
    if (i < beats.length && beats[i].chord === beats[start].chord) continue;
    chords.push({
      label: beats[start].chord,
      start_beat: start, end_beat: i,
      start: beats[start].t,
      end: i < beats.length ? beats[i].t : analysis.duration,
      implied: beats[start].implied,
    });
    start = i;
  }
  return { ...analysis, beats, chords };
}

/**
 * Ask for a song to be analysed.
 *
 * No URL goes in: the catalogue knows the artist, title and running time, and
 * the worker is the only part of the system with yt-dlp, so it decides which
 * recording that actually is. Safe to call twice — the database returns the
 * existing row rather than queueing a duplicate.
 */
export async function requestSong(track: {
  catalogId: string; slug: string; title: string; artist: string;
  duration?: number | null; artwork?: string | null;
}): Promise<SongRow | null> {
  const { data, error } = await supabase.rpc("request_song", {
    p_id: track.catalogId,
    p_slug: track.slug,
    p_url: null,
    p_title: track.title,
    p_artist: track.artist,
    p_duration: track.duration ?? null,
    p_artwork: track.artwork ?? null,
  });
  if (error) throw error;
  return data as SongRow | null;
}

/** A song row exists before the worker has titled it, so every screen needs a
 *  sensible stand-in rather than a blank line or the word "null". */
export function titleOf(row: { title: string | null; slug: string }): string {
  if (row.title) return row.title;
  return row.slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

export function artistOf(row: { artist: string | null }): string {
  return row.artist ?? "Unknown artist";
}

/**
 * Is this song actually finished?
 *
 * status goes to "done" when the CHORDS are published, which is a minute
 * before the words are measured. Treating that as finished showed "Ready" on
 * songs whose chart could not be drawn yet.
 */
export function isReady(song: { status: string; lyrics_mode: string | null }): boolean {
  return song.status === "done"
    && !!song.lyrics_mode
    && song.lyrics_mode !== "pending";
}
